// ====================================================
// BLOKAS 1: IMPORT STATEMENTS
// Failas: ExchangesStrategy.tsx
// ====================================================
console.log('🚀 Blokas 1: Import statements [ExchangesStrategy.tsx]');

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Info, Copy, ExternalLink, AlertTriangle, Percent, Save } from 'lucide-react';
import { Button } from '../../ui/Button';
import { cn } from '../../utils/cn';
import { useApiKeys } from '../../hooks/useApiKeys';
import { toast } from 'sonner';
import { buildPatchFromChange } from '../../utils/patchMap';
import { useAuth } from '../../contexts/AuthContext';

console.log('✅ Blokas 1: Importai užkrauti [ExchangesStrategy.tsx]');

// ====================================================
// BLOKAS 2: INTERFACE DEFINITION
// Failas: ExchangesStrategy.tsx
// ====================================================
console.log('🚀 Blokas 2: Interface apibrėžimas [ExchangesStrategy.tsx]');

interface ExchangesStrategyProps {
  botId?: string;
  exchange: string;
  setExchange: (exchange: string) => void;
  strategy: string;
  setStrategy: (strategy: string) => void;
  deploymentTarget: 'live' | 'paper';
  setDeploymentTarget: (target: 'live' | 'paper') => void;
  exchangeFeePercentage: number;
  setExchangeFeePercentage: (percentage: number) => void;
}

console.log('✅ Blokas 2: Interface sukurtas [ExchangesStrategy.tsx]');

// ====================================================
// BLOKAS 3: MAIN COMPONENT FUNCTION
// Failas: ExchangesStrategy.tsx
// ====================================================
console.log('🚀 Blokas 3: Pagrindinė komponento funkcija [ExchangesStrategy.tsx]');

export const ExchangesStrategy: React.FC<ExchangesStrategyProps> = ({
  botId,
  exchange,
  setExchange,
  strategy,
  setStrategy,
  deploymentTarget,
  setDeploymentTarget,
  exchangeFeePercentage,
  setExchangeFeePercentage,
}) => {
  console.log('🔹 Sub-blokas 3.1: Komponento funkcija inicijuota [ExchangesStrategy.tsx]');

  // ====================================================
  // BLOKAS 4: STATE AND HOOKS
  // Failas: ExchangesStrategy.tsx
  // ====================================================
  console.log('🚀 Blokas 4: State ir hooks [ExchangesStrategy.tsx]');
  
  const { apiKeys, addApiKey } = useApiKeys(); // API KEY FRAGMENT LOGIC: Get existing API keys
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState<string>('');
  const [apiSecret, setApiSecret] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [showDeploymentTargetTooltip, setShowDeploymentTargetTooltip] = useState(false);
  const [showExchangeFeeTooltip, setShowExchangeFeeTooltip] = useState(false);

  console.log('🔹 Sub-blokas 4.1: State kintamieji inicijuoti [ExchangesStrategy.tsx]');
  console.log(`   - apiKeys: ${apiKeys?.length || 0} raktai`);
  console.log(`   - deploymentTarget: ${deploymentTarget}`);
  console.log(`   - exchange: ${exchange}`);

  // ====================================================
  // BLOKAS 5: CONSTANTS AND STATIC DATA
  // Failas: ExchangesStrategy.tsx
  // ====================================================
  console.log('🚀 Blokas 5: Konstantos ir statiniai duomenys [ExchangesStrategy.tsx]');

  const exchanges = [
    { value: 'bybit', label: 'Bybit' },
    { value: 'binance', label: 'Binance' },
    { value: 'coinbase', label: 'Coinbase' },
    { value: 'kraken', label: 'Kraken' },
    { value: 'kucoin', label: 'KuCoin' }
  ];

  const ipAddresses = '54.194.236.69, 34.247.157.15, 108.129.50.219, 34.255.23.52, 3.251.100.11, 52.214.84.203';

  console.log('🔹 Sub-blokas 5.1: Mainų platformos sąrašas sukurtas [ExchangesStrategy.tsx]');
  console.log('🔹 Sub-blokas 5.2: IP adresų sąrašas sukurtas [ExchangesStrategy.tsx]');

  // ====================================================
  // BLOKAS 6: API KEY FRAGMENT LOGIC
  // Failas: ExchangesStrategy.tsx
  // ====================================================
  console.log('🚀 Blokas 6: API rakto fragmento logika [ExchangesStrategy.tsx]');

  // API KEY FRAGMENT LOGIC: Get existing API key for current bot and exchange
  const existingApiKey = botId && exchange ? 
    apiKeys.find(key => key.bot_id === botId && key.exchange_name === exchange) : null;

  console.log('🔹 Sub-blokas 6.1: Esamas API raktas rastas [ExchangesStrategy.tsx]', {
    hasExistingKey: !!existingApiKey,
    botId,
    exchange
  });

  // API KEY FRAGMENT LOGIC: Helper function to show API key fragment
  const getApiKeyFragment = (fullKey: string): string => {
    console.log('🔸 Sub-blokas 6.1.1: API rakto fragmento generavimas [ExchangesStrategy.tsx]');
    if (!fullKey || fullKey.length < 12) return fullKey;
    return fullKey.substring(0, 12) + '...';
  };

  // API KEY FRAGMENT LOGIC: Helper function to get display value for API key input
  const getApiKeyDisplayValue = (): string => {
    console.log('🔸 Sub-blokas 6.1.2: API rakto rodymo vertės gavimas [ExchangesStrategy.tsx]');
    if (apiKey) return apiKey; // User is typing
    if (existingApiKey) return getApiKeyFragment(existingApiKey.api_key_public);
    return '';
  };

  // API SECRET LOGIC: Helper function to get display value for API secret input
  const getApiSecretDisplayValue = (): string => {
    console.log('🔸 Sub-blokas 6.1.3: API secret rodymo vertės gavimas [ExchangesStrategy.tsx]');
    if (apiSecret) return apiSecret; // User is typing
    if (existingApiKey) return '••••••••••••••••••••'; // Fixed 20 dots
    return '';
  };

  // ====================================================
  // BLOKAS 7: EVENT HANDLERS
  // Failas: ExchangesStrategy.tsx
  // ====================================================
  console.log('🚀 Blokas 7: Event handlers [ExchangesStrategy.tsx]');

  const handleCopyIPs = () => {
    console.log('🔹 Sub-blokas 7.1: IP adresų kopijavimas [ExchangesStrategy.tsx]');
    navigator.clipboard.writeText(ipAddresses);
    toast.success('IP addresses copied to clipboard');
    console.log('✅ IP adresai nukopijuoti [ExchangesStrategy.tsx]');
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔹 Sub-blokas 7.2: API rakto keitimas [ExchangesStrategy.tsx]');
    setApiKey(e.target.value);
  };

  const handleApiSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔹 Sub-blokas 7.3: API secret keitimas [ExchangesStrategy.tsx]');
    setApiSecret(e.target.value);
  };

  const handleConnectExchange = async () => {
    console.log('🔹 Sub-blokas 7.4: Mainų platformos prijungimas [ExchangesStrategy.tsx]');
    
    if (!botId) {
      console.log('⚠️ Klaida: Botas neišsaugotas [ExchangesStrategy.tsx]');
      setIsConnecting(true);
      toast.error('Please save the bot first before connecting to an exchange', {
        duration: 5000,
        dismissible: true
      });
      setIsConnecting(false);
      return;
    }
    
    if (!exchange) {
      console.log('⚠️ Klaida: Nepasirinkta mainų platforma [ExchangesStrategy.tsx]');
      setIsConnecting(true);
      toast.error('Please select an exchange');
      setIsConnecting(false);
      return;
    }
    
    if (!apiKey.trim() || !apiSecret.trim()) {
      console.log('⚠️ Klaida: Trūksta API raktų [ExchangesStrategy.tsx]');
      setIsConnecting(true);
      toast.error('API Key and Secret are required');
      setIsConnecting(false);
      return;
    }
    
    setIsConnecting(true);
    console.log(`🔗 Bandoma prijungti prie ${exchange} [ExchangesStrategy.tsx]`);
    
    const success = await addApiKey(botId, exchange, apiKey.trim(), apiSecret.trim());
    setIsConnecting(false);
    
    if (success) {
      console.log('✅ API raktai sėkmingai pridėti [ExchangesStrategy.tsx]');
      // Clear the form fields after successful addition
      setApiKey('');
      setApiSecret('');
    } else {
      console.log('❌ Nepavyko pridėti API raktų [ExchangesStrategy.tsx]');
    }
  };

  // ✅ Handle deployment target toggle (live ⇄ paper)
  const handleDeploymentTargetToggle = () => {
    console.log('🔹 Sub-blokas 7.5: Deployment target perjungimas [ExchangesStrategy.tsx]');
    const newTarget = deploymentTarget === 'live' ? 'paper' : 'live';
    console.log(`🔄 Keičiama iš ${deploymentTarget} į ${newTarget} [ExchangesStrategy.tsx]`);
    setDeploymentTarget(newTarget);
  };

  // ✅ Compute isPractice for UI (derived from deploymentTarget)
  const isPractice = deploymentTarget === 'paper';
  console.log(`🔸 Sub-blokas 7.5.1: isPractice apskaičiuotas: ${isPractice} [ExchangesStrategy.tsx]`);

  // Handle exchange selection
  const handleExchangeChange = (newExchange: string) => {
    console.log('🔹 Sub-blokas 7.6: Mainų platformos keitimas [ExchangesStrategy.tsx]');
    console.log(`🔄 Keičiama mainų platforma į: ${newExchange} [ExchangesStrategy.tsx]`);
    setExchange(newExchange);
  };

  // Handle exchange fee percentage change
  const handleExchangeFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔹 Sub-blokas 7.7: Mainų mokesčio keitimas [ExchangesStrategy.tsx]');
    const value = e.target.value;
    console.log(`📊 Gauta mokesčio reikšmė: ${value} [ExchangesStrategy.tsx]`);
    
    // Handle empty string by setting to default
    if (value === '') {
      console.log('🔁 Nustatomas numatytasis mokestis: 0.055 [ExchangesStrategy.tsx]');
      setExchangeFeePercentage(0.055);
    } else {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
        console.log(`✅ Nustatomas mokestis: ${numericValue} [ExchangesStrategy.tsx]`);
        setExchangeFeePercentage(numericValue);
      } else if (!isNaN(numericValue) && numericValue > 100) {
        // Cap at 100%
        console.log('⚠️ Mokestis virš 100%, nustatoma 100 [ExchangesStrategy.tsx]');
        setExchangeFeePercentage(100);
      } else {
        // Invalid input, keep current value or set to default
        console.log('⚠️ Netinkama reikšmė, išlaikoma esama [ExchangesStrategy.tsx]');
        setExchangeFeePercentage(prev => (typeof prev === 'number' && !isNaN(prev)) ? prev : 0.055);
      }
    }
  };

  // Add a separate handler for when the input loses focus to ensure valid value
  const handleExchangeFeeBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔹 Sub-blokas 7.8: Mainų mokesčio blur event [ExchangesStrategy.tsx]');
    const inputValue = e.target.value;
    const value = parseFloat(inputValue);
    console.log(`🔍 Tikrinama mokesčio reikšmė: ${inputValue} [ExchangesStrategy.tsx]`);

    if (inputValue === '' || isNaN(value) || value < 0) {
      // Reset to default if invalid
      console.log('🔁 Netinkama reikšmė, nustatomas numatytasis: 0.055 [ExchangesStrategy.tsx]');
      setExchangeFeePercentage(0.055);
    } else if (value > 100) {
      // Cap at 100%
      console.log('⚠️ Mokestis virš 100%, nustatoma 100 [ExchangesStrategy.tsx]');
      setExchangeFeePercentage(100);
    }
  };

  // ====================================================
  // BLOKAS 8: JSX RETURN
  // Failas: ExchangesStrategy.tsx
  // ====================================================
  console.log('🚀 Blokas 8: JSX grąžinimas [ExchangesStrategy.tsx]');

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        
        {/* HEADER SECTION */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Exchange Settings</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure your trading exchange connection</p>
          </div>
          <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
            <Building2 className="h-6 w-6 text-purple-500" />
          </div>
        </div>

        <div className="space-y-8">
          {/* ==================================================== */}
          {/* SUB-BLOKAS 8.1: DEPLOYMENT TARGET SECTION */}
          {/* Failas: ExchangesStrategy.tsx */}
          {/* ==================================================== */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Deployment Target
                </h3>
                <div className="relative">
                  <Info 
                    className="h-4 w-4 text-gray-400 cursor-help"
                    onMouseEnter={() => {
                      console.log('🔸 Sub-blokas 8.1.1: Deployment target tooltip rodymas [ExchangesStrategy.tsx]');
                      setShowDeploymentTargetTooltip(true);
                    }}
                    onMouseLeave={() => {
                      console.log('🔸 Sub-blokas 8.1.2: Deployment target tooltip paslėpimas [ExchangesStrategy.tsx]');
                      setShowDeploymentTargetTooltip(false);
                    }}
                  />
                  
                  {/* Tooltip for Deployment Target */}
                  {showDeploymentTargetTooltip && (
                    <div className="absolute z-50 w-96 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-xl" 
                         style={{ bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '10px' }}>
                      <div className="flex flex-col space-y-4">
                        {/* Explanation */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                            <h5 className="font-medium text-blue-400">Deployment Target</h5>
                          </div>
                          <p className="text-sm text-gray-300 ml-5 mb-3">
                            This setting determines where your bot will be deployed when you activate it from backtest results. 
                            Backtest is an action (testing with historical data), not a deployment mode.
                          </p>
                        </div>

                        {/* Paper Trading Section */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                            <h5 className="font-medium text-orange-400">Paper Trading</h5>
                          </div>
                          <ul className="space-y-2 text-sm text-gray-300 ml-5">
                            <li>• Practice with virtual funds - no real money at risk</li>
                            <li>• Test strategies using real market data</li>
                            <li>• Perfect for beginners and strategy testing</li>
                            <li>• Unlimited virtual balance for testing</li>
                            <li>• Simulates exchange fees for realistic results</li>
                          </ul>
                        </div>

                        {/* Live Trading Section */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded-full bg-green-400"></div>
                            <h5 className="font-medium text-green-400">Live Trading</h5>
                          </div>
                          <ul className="space-y-2 text-sm text-gray-300 ml-5">
                            <li>• Trade with real funds on the exchange</li>
                            <li>• Requires API key configuration</li>
                            <li>• Real profits and losses</li>
                            <li>• Recommended only after successful paper trading</li>
                            <li>• Actual exchange fees apply</li>
                          </ul>
                        </div>
                      </div>
                      
                      {/* Tooltip arrow */}
                      <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-900 rotate-45"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-sm font-medium transition-colors",
                    isPractice 
                      ? "text-orange-500 dark:text-orange-400" 
                      : "text-gray-400 dark:text-gray-500" 
                  )}>
                    Paper
                  </span>
                  <button
                    onClick={handleDeploymentTargetToggle}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                      isPractice 
                        ? "bg-orange-500" 
                        : "bg-green-500"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform",
                        isPractice ? "translate-x-1" : "translate-x-6"
                      )}
                    />
                  </button>
                  <span className={cn(
                    "text-sm font-medium transition-colors",
                    !isPractice 
                      ? "text-green-500 dark:text-green-400"
                      : "text-gray-400 dark:text-gray-500"
                  )}>
                    Live
                  </span>
                </div>
                <div className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                  isPractice
                    ? "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400"
                    : "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                )}>
                  {isPractice ? 'Paper Target' : 'Live Target'}
                </div>
              </div>
            </div>
            
            {/* Mode Description */}
            <div className={cn(
              "p-4 rounded-lg transition-colors",
              isPractice
                ? "bg-orange-50 dark:bg-orange-900/10"
                : "bg-green-50 dark:bg-green-900/10"
            )}>
              <div className="flex items-start gap-3">
                {isPractice ? (
                  <Info className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={cn(
                    "font-medium mb-1",
                    isPractice
                      ? "text-orange-700 dark:text-orange-400"
                      : "text-green-700 dark:text-green-400"
                  )}>
                    {isPractice 
                      ? 'Paper Trading Target' 
                      : 'Live Trading Target'}
                  </p>
                  <p className={cn(
                    "text-sm",
                    isPractice
                      ? "text-orange-600 dark:text-orange-300"
                      : "text-green-600 dark:text-green-300"
                  )}>
                    {isPractice
                      ? 'When activated from backtest results, the bot will be deployed in Paper Trading mode with virtual funds. Perfect for testing strategies without financial risk.'
                      : 'When activated from backtest results, the bot will be deployed in Live Trading mode with real funds. Ensure your strategy is thoroughly tested before using this mode.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ==================================================== */}
          {/* SUB-BLOKAS 8.2: EXCHANGE SELECTION */}
          {/* Failas: ExchangesStrategy.tsx */}
          {/* ==================================================== */}
          <div className="space-y-2 mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Exchange
            </label>
            <div className="relative">
              <select
                value={exchange}
                onChange={(e) => handleExchangeChange(e.target.value)}
                className={cn(
                  "w-full px-4 py-2 rounded-lg border appearance-none",
                  "bg-white dark:bg-gray-700",
                  "border-gray-300 dark:border-gray-600",
                  "text-gray-900 dark:text-white",
                  "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                  "cursor-pointer"
                )}
              >
                <option value="">Select an exchange</option>
                {exchanges.map(ex => (
                  <option key={ex.value} value={ex.value}>{ex.label}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* ==================================================== */}
          {/* SUB-BLOKAS 8.3: EXCHANGE FEE PERCENTAGE */}
          {/* Failas: ExchangesStrategy.tsx */}
          {/* ==================================================== */}
          <div className="space-y-2 mb-6">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Exchange Fee Percentage
              </label>
              <div className="relative">
                <Info 
                  className="h-4 w-4 text-gray-400 cursor-help"
                  onMouseEnter={() => {
                    console.log('🔸 Sub-blokas 8.3.1: Exchange fee tooltip rodymas [ExchangesStrategy.tsx]');
                    setShowExchangeFeeTooltip(true);
                  }}
                  onMouseLeave={() => {
                    console.log('🔸 Sub-blokas 8.3.2: Exchange fee tooltip paslėpimas [ExchangesStrategy.tsx]');
                    setShowExchangeFeeTooltip(false);
                  }}
                />
                
                {/* Tooltip for Exchange Fee */}
                {showExchangeFeeTooltip && (
                  <div className="absolute z-50 w-96 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-xl"
                       style={{ bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '10px' }}>
                    <div className="flex flex-col space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                          <h5 className="font-medium text-blue-400">What are trading fees?</h5>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-300 ml-5">
                          <li>• Fees charged by exchanges for executing trades</li>
                          <li>• Usually a percentage of the trade value</li>
                          <li>• Can vary based on trading volume and VIP levels</li>
                          <li>• Different for maker vs. taker orders</li>
                        </ul>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-green-400"></div>
                          <h5 className="font-medium text-green-400">Why it matters</h5>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-300 ml-5">
                          <li>• Affects your net profit calculation</li>
                          <li>• Important for accurate backtesting</li>
                          <li>• Helps determine minimum profitable trade size</li>
                          <li>• Critical for high-frequency trading strategies</li>
                        </ul>
                      </div>

                      <div className="flex items-start gap-2 bg-yellow-900/20 p-2 rounded">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-yellow-300 text-xs">
                          Enter the exact fee percentage from your exchange. For example, if your fee is 0.055%, enter "0.055" (not 0.00055).
                        </p>
                      </div>
                    </div>
                    
                    {/* Tooltip arrow */}
                    <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Percent className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                value={exchangeFeePercentage}
                onChange={handleExchangeFeeChange}
                onBlur={handleExchangeFeeBlur}
                step="0.001"
                min="0"
                max="100"
                className={cn(
                  "w-full px-4 py-2 pl-10 rounded-lg border",
                  "bg-white dark:bg-gray-700",
                  "border-gray-300 dark:border-gray-600",
                  "text-gray-900 dark:text-white",
                  "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                  "placeholder-gray-500 dark:placeholder-gray-400"
                )}
                placeholder="Enter exchange fee percentage (e.g., 0.055)"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter the exact fee percentage (e.g., for 0.055% enter 0.055). This affects profit/loss calculations.
            </p>
            {exchangeFeePercentage > 1 && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Warning: Fee percentage seems high ({exchangeFeePercentage}%). Typical exchange fees are 0.01-0.1%.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ==================================================== */}
          {/* SUB-BLOKAS 8.4: API CONNECTION SECTION */}
          {/* Failas: ExchangesStrategy.tsx */}
          {/* ==================================================== */}
          {deploymentTarget === 'live' && botId && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">API Connection</h3>
                <Button 
                  variant="outline"
                  size="sm"
                  icon={<ExternalLink className="h-4 w-4" />}
                  iconPosition="right"
                >
                  Get API Keys
                </Button>
              </div>

              {/* IP Whitelist Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-700 dark:text-blue-300 text-sm">
                      For security reasons, you need to whitelist these IP addresses in your exchange settings:
                    </p>
                    <p className="text-blue-600 dark:text-blue-400 text-xs">
                      This allows our trading servers to execute trades on your behalf.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded border border-blue-200 dark:border-blue-800">
                  <code className="text-sm text-blue-600 dark:text-blue-400 flex-1 overflow-x-auto">
                    {ipAddresses}
                  </code>
                  <button
                    onClick={handleCopyIPs}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {!botId && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800/30 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400 mb-1">
                        Save Bot First
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Save the bot with basic information (name, exchange, trading mode) first, then you can add API keys. Click "Save Bot" in the sidebar to continue.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* API Key Input */}
              <div className="space-y-2">
                {/* API KEY FRAGMENT LOGIC: Show fragment in label when key exists */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  API Key
                  {existingApiKey && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (Current: {getApiKeyFragment(existingApiKey.api_key_public)})
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={getApiKeyDisplayValue()}
                  onChange={handleApiKeyChange}
                  placeholder={existingApiKey ? "Enter new API key to replace existing" : "Enter your exchange API key"}
                  disabled={!botId}
                  className={cn(
                    "w-full px-4 py-2 rounded-lg border",
                    "bg-white dark:bg-gray-700",
                    "border-gray-300 dark:border-gray-600",
                    "text-gray-900 dark:text-white",
                    "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    "placeholder-gray-500 dark:placeholder-gray-400",
                    !botId && "opacity-50 cursor-not-allowed"
                  )}
                />
              </div>

              {/* API Secret Input */}
              <div className="space-y-2">
                {/* API SECRET LOGIC: Show status in label when secret exists */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  API Secret
                  {existingApiKey && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (Current: configured)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={getApiSecretDisplayValue()}
                  onChange={handleApiSecretChange}
                  placeholder={existingApiKey ? "Enter new API secret to replace existing" : "Enter your exchange API secret"}
                  disabled={!botId}
                  className={cn(
                    "w-full px-4 py-2 rounded-lg border",
                    "bg-white dark:bg-gray-700",
                    "border-gray-300 dark:border-gray-600",
                    "text-gray-900 dark:text-white",
                    "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    "placeholder-gray-500 dark:placeholder-gray-400",
                    !botId && "opacity-50 cursor-not-allowed"
                  )}
                />
              </div>

              {/* Connect Button */}
              <Button
                variant="primary"
                className="w-full flex items-center justify-center gap-2"
                size="lg"
                isLoading={isConnecting}
                onClick={handleConnectExchange}
                disabled={!botId}
              >
                {!botId ? (
                  <>
                    <Save className="h-5 w-5" />
                    Save Bot First
                  </>
                ) : (
                  <>Connect to {exchange || 'Exchange'}</>
                )}
              </Button>
              
              {!botId && (
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                  API keys can only be added to saved bots
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

console.log('✅ Blokas 8: JSX sėkmingai grąžintas [ExchangesStrategy.tsx]');
console.log('🎯 Visas komponentas ExchangesStrategy užkrautas [ExchangesStrategy.tsx]');